// src/pages/Signup.tsx
// Step 1 of 5 - Email entry
// Styled to match app dashboard

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ENV } from "@/lib/env";
import { supabase } from "@/lib/supabase";

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i < current 
              ? "w-8 bg-[#a78bfa]" 
              : i === current 
                ? "w-8 bg-[#a78bfa]/40" 
                : "w-3 bg-[#f1f5f9]"
          }`}
        />
      ))}
    </div>
  );
}

export default function Signup() {
  const [params] = useSearchParams();
  const partner = params.get("partner") || localStorage.getItem("partner") || "";
  const referral = params.get("ref") || localStorage.getItem("referral_code") || "";

  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);

  const nav = useNavigate();

  useEffect(() => {
    if (partner) localStorage.setItem("partner", partner);
    if (referral) localStorage.setItem("referral_code", referral);
  }, [partner, referral]);

  const sendCode = async () => {
    if (sending) return;
    setErr(null);

    if (!isEmail(email)) {
      return setErr("Please enter a valid email address.");
    }

    if (!agreedTerms) {
      return setErr("Please agree to the terms to continue.");
    }

    try {
      setSending(true);

      const res = await fetch(`${ENV.EDGE_BASE}/auth-email-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to start login");

      const mode = json.mode as "signup" | "login";

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: mode === "signup" },
      });

      if (error) {
        if ((error as any).status === 429) {
          throw new Error("Too many attempts. Please wait a minute.");
        }
        throw error;
      }

      localStorage.setItem("signup_email", email);
      localStorage.setItem("otp_type", mode);
      nav(`/check-email${partner ? `?partner=${encodeURIComponent(partner)}` : ""}`);
    } catch (e: any) {
      setErr(e.message || "Failed to send code");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <Link
          to="/"
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              Get Started
            </h1>
            <p className="text-gray-500 text-sm">
              Enter your email to begin
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5">
            
            {/* Email Input */}
            <div className="mb-4">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] text-base placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                autoFocus
              />
            </div>

            {/* Terms Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group mb-5">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
                  agreedTerms 
                    ? "bg-[#a78bfa] border-[#a78bfa]" 
                    : "border-[#e2e8f0] group-hover:border-[#e2e8f0] bg-[#f1f5f9]"
                }`}>
                  {agreedTerms && (
                    <svg className="w-3 h-3 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-500 leading-relaxed">
                I agree to the{" "}
                <Link 
                  to="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0f172a] hover:text-[#a78bfa] transition-colors"
                >
                  Terms of Service
                </Link>
                {" "}and{" "}
                <Link 
                  to="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0f172a] hover:text-[#a78bfa] transition-colors"
                >
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* Continue Button */}
            <button
              onClick={sendCode}
              disabled={sending || !email || !agreedTerms}
              className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending...
                </>
              ) : "Continue"}
            </button>

            {/* Error */}
            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}
          </div>

          {/* Step Indicator */}
          <div className="mt-10">
            <StepIndicator current={0} total={5} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
